import React, { useState, useEffect } from 'react';
import authService from '../services/auth.service';
import {
    AccessTime as ClockIcon,
    CheckCircle as CheckCircleIcon,
    Cancel as XCircleIcon,
    Refresh as ArrowPathIcon,
    Description as DocumentTextIcon,
    PlayArrow as PlayIcon,
    Pause as PauseIcon,
    Delete as TrashIcon
} from '@mui/icons-material';

interface QueueDocument {
    _id: string;
    fileSyncId: {
        _id: string;
        fileName: string;
        fileSize: number;
        mimeType: string;
        machineId: string;
    };
    status: 'queued' | 'processing' | 'completed' | 'failed';
    processingTime?: number;
    errorMessage?: string;
    ocrConfidence?: number;
    metadata?: {
        pageCount?: number;
        wordCount?: number;
    };
    createdAt: string;
    processedAt?: string;
}

interface QueueStats {
    queue: {
        waiting: number;
        active: number;
        completed: number;
        failed: number;
        delayed: number;
        total: number;
    };
    documents: {
        queued: number;
        processing: number;
        completed: number;
        failed: number;
    };
    performance: {
        avgProcessingTime: number;
        minProcessingTime: number;
        maxProcessingTime: number;
        totalProcessed: number;
        avgConfidence: number;
    };
}

const Queue: React.FC = () => {
    const [documents, setDocuments] = useState<QueueDocument[]>([]);
    const [stats, setStats] = useState<QueueStats | null>(null);
    const [loading, setLoading] = useState(true);
    const [selectedStatus, setSelectedStatus] = useState<string>('all');
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    useEffect(() => {
        fetchQueueData();
        const interval = setInterval(fetchQueueData, 5000); // Refresh every 5 seconds
        return () => clearInterval(interval);
    }, [selectedStatus, page]);

    const fetchQueueData = async () => {
        try {
            const api = authService.getApiInstance();
            const [docsRes, statsRes] = await Promise.all([
                api.get(`/queue/documents?status=${selectedStatus}&page=${page}`),
                api.get('/queue/stats')
            ]);

            setDocuments(docsRes.data.documents);
            setTotalPages(docsRes.data.pagination.pages);
            setStats(statsRes.data);
        } catch (error) {
            console.error('Failed to fetch queue data:', error);
        } finally {
            setLoading(false);
        }
    };

    const retryDocument = async (documentId: string) => {
        try {
            const api = authService.getApiInstance();
            await api.post(`/queue/retry/${documentId}`);
            fetchQueueData();
        } catch (error) {
            console.error('Failed to retry document:', error);
        }
    };

    const processAll = async () => {
        try {
            const api = authService.getApiInstance();
            const response = await api.post('/queue/process-all');
            alert(`Queued ${response.data.successful} documents for processing`);
            fetchQueueData();
        } catch (error) {
            console.error('Failed to process all documents:', error);
        }
    };

    const formatFileSize = (bytes: number): string => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const formatDuration = (ms: number): string => {
        if (!ms || isNaN(ms)) return '-';
        if (ms < 1000) return `${Math.round(ms)}ms`;
        if (ms < 60000) return `${Math.round(ms / 1000)}s`;
        return `${Math.round(ms / 60000)}m`;
    };

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'queued':
                return <ClockIcon sx={{ width: 20, height: 20, color: '#6b7280' }} />;
            case 'processing':
                return <ArrowPathIcon sx={{ width: 20, height: 20, color: '#3b82f6' }} className="animate-spin" />;
            case 'completed':
                return <CheckCircleIcon sx={{ width: 20, height: 20, color: '#10b981' }} />;
            case 'failed':
                return <XCircleIcon sx={{ width: 20, height: 20, color: '#ef4444' }} />;
            default:
                return <DocumentTextIcon sx={{ width: 20, height: 20, color: '#6b7280' }} />;
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'queued':
                return 'bg-gray-100 text-gray-800';
            case 'processing':
                return 'bg-blue-100 text-blue-800';
            case 'completed':
                return 'bg-green-100 text-green-800';
            case 'failed':
                return 'bg-red-100 text-red-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="p-4 sm:p-6 max-w-7xl mx-auto">
            {/* Header */}
            <div className="mb-6 sm:mb-8">
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Document Processing Queue</h1>
                <p className="text-sm sm:text-base text-gray-600 mt-2">
                    Monitor and manage document processing tasks
                </p>
            </div>

            {/* Statistics */}
            {stats && (
                <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4 mb-6 sm:mb-8">
                    <div className="bg-white rounded-lg shadow-sm p-3 sm:p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-xs sm:text-sm font-medium text-gray-600">Queued</p>
                                <p className="text-xl sm:text-2xl font-bold text-gray-900">{stats.documents.queued}</p>
                            </div>
                            <ClockIcon sx={{ width: { xs: 24, sm: 32 }, height: { xs: 24, sm: 32 }, color: '#9ca3af' }} />
                        </div>
                    </div>
                    <div className="bg-white rounded-lg shadow-sm p-3 sm:p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-xs sm:text-sm font-medium text-gray-600">Processing</p>
                                <p className="text-xl sm:text-2xl font-bold text-blue-600">{stats.documents.processing}</p>
                            </div>
                            <ArrowPathIcon sx={{ width: { xs: 24, sm: 32 }, height: { xs: 24, sm: 32 }, color: '#60a5fa' }} className="animate-spin" />
                        </div>
                    </div>
                    <div className="bg-white rounded-lg shadow-sm p-3 sm:p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-xs sm:text-sm font-medium text-gray-600">Completed</p>
                                <p className="text-xl sm:text-2xl font-bold text-green-600">{stats.documents.completed}</p>
                            </div>
                            <CheckCircleIcon sx={{ width: { xs: 24, sm: 32 }, height: { xs: 24, sm: 32 }, color: '#4ade80' }} />
                        </div>
                    </div>
                    <div className="bg-white rounded-lg shadow-sm p-3 sm:p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-xs sm:text-sm font-medium text-gray-600">Failed</p>
                                <p className="text-xl sm:text-2xl font-bold text-red-600">{stats.documents.failed}</p>
                            </div>
                            <XCircleIcon sx={{ width: { xs: 24, sm: 32 }, height: { xs: 24, sm: 32 }, color: '#f87171' }} />
                        </div>
                    </div>
                    <div className="bg-white rounded-lg shadow-sm p-3 sm:p-4 col-span-2 sm:col-span-1">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-xs sm:text-sm font-medium text-gray-600">Avg Time</p>
                                <p className="text-xl sm:text-2xl font-bold text-gray-900">
                                    {formatDuration(stats.performance.avgProcessingTime)}
                                </p>
                            </div>
                            <ClockIcon sx={{ width: { xs: 24, sm: 32 }, height: { xs: 24, sm: 32 }, color: '#9ca3af' }} />
                        </div>
                    </div>
                </div>
            )}

            {/* Controls */}
            <div className="bg-white rounded-lg shadow-sm p-3 sm:p-4 mb-4 sm:mb-6">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                    <div className="flex items-center space-x-2 sm:space-x-4 w-full sm:w-auto">
                        <select
                            value={selectedStatus}
                            onChange={(e) => {
                                setSelectedStatus(e.target.value);
                                setPage(1);
                            }}
                            className="flex-1 sm:flex-none px-3 sm:px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm sm:text-base"
                        >
                            <option value="all">All Documents</option>
                            <option value="queued">Queued</option>
                            <option value="processing">Processing</option>
                            <option value="completed">Completed</option>
                            <option value="failed">Failed</option>
                        </select>
                        <button
                            onClick={fetchQueueData}
                            className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
                        >
                            <ArrowPathIcon sx={{ width: 20, height: 20 }} />
                        </button>
                    </div>
                    <button
                        onClick={processAll}
                        className="w-full sm:w-auto px-3 sm:px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center space-x-2 text-sm sm:text-base"
                    >
                        <PlayIcon sx={{ width: 20, height: 20 }} />
                        <span className="whitespace-nowrap">Process All</span>
                        <span className="hidden sm:inline">Unprocessed</span>
                    </button>
                </div>
            </div>

            {/* Documents List */}
            <div className="bg-white rounded-lg shadow-sm">
                {documents.length === 0 ? (
                    <div className="text-center py-12">
                        <DocumentTextIcon sx={{ width: 48, height: 48, color: '#9ca3af', margin: '0 auto' }} />
                        <p className="mt-2 text-sm text-gray-500">No documents found</p>
                    </div>
                ) : (
                    <>
                {/* Mobile View - Cards */}
                <div className="sm:hidden">
                    {documents.map((doc) => (
                        <div key={doc._id} className="p-4 border-b border-gray-200 last:border-b-0">
                            <div className="flex items-start justify-between mb-2">
                                <div>
                                    <div className="text-sm font-medium text-gray-900">
                                        {doc.fileSyncId?.fileName || 'Unknown'}
                                    </div>
                                    <div className="text-xs text-gray-500">
                                        Machine: {doc.fileSyncId?.machineId}
                                    </div>
                                </div>
                                <div className="flex items-center">
                                    {getStatusIcon(doc.status)}
                                    <span className={`ml-1 px-2 py-1 text-xs rounded-full ${getStatusColor(doc.status)}`}>
                                        {doc.status}
                                    </span>
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-2 text-xs text-gray-500 mb-2">
                                <div>
                                    <span className="font-medium">Size:</span> {formatFileSize(doc.fileSyncId?.fileSize || 0)}
                                </div>
                                <div>
                                    <span className="font-medium">Type:</span> {doc.fileSyncId?.mimeType?.split('/').pop() || 'Unknown'}
                                </div>
                                <div>
                                    <span className="font-medium">Time:</span> {doc.processingTime ? formatDuration(doc.processingTime) : '-'}
                                </div>
                                <div>
                                    <span className="font-medium">Confidence:</span> {doc.ocrConfidence ? `${doc.ocrConfidence.toFixed(1)}%` : '-'}
                                </div>
                            </div>
                            {doc.status === 'failed' && (
                                <button
                                    onClick={() => retryDocument(doc._id)}
                                    className="text-blue-600 hover:text-blue-900 flex items-center space-x-1 text-sm"
                                >
                                    <ArrowPathIcon sx={{ width: 16, height: 16 }} />
                                    <span>Retry</span>
                                </button>
                            )}
                            {doc.status === 'completed' && (
                                <button className="text-green-600 hover:text-green-900 text-sm">
                                    View
                                </button>
                            )}
                        </div>
                    ))}
                </div>

                {/* Desktop View - Table */}
                <div className="hidden sm:block overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Status
                                </th>
                                <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    File Name
                                </th>
                                <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">
                                    Size
                                </th>
                                <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden lg:table-cell">
                                    Type
                                </th>
                                <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Processing Time
                                </th>
                                <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">
                                    Confidence
                                </th>
                                <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {documents.map((doc) => (
                                <tr key={doc._id} className="hover:bg-gray-50">
                                    <td className="px-4 lg:px-6 py-4 whitespace-nowrap">
                                        <div className="flex items-center">
                                            {getStatusIcon(doc.status)}
                                            <span className={`ml-2 px-2 py-1 text-xs rounded-full ${getStatusColor(doc.status)}`}>
                                                {doc.status}
                                            </span>
                                        </div>
                                    </td>
                                    <td className="px-4 lg:px-6 py-4">
                                        <div className="text-sm text-gray-900 truncate max-w-xs lg:max-w-sm">
                                            {doc.fileSyncId?.fileName || 'Unknown'}
                                        </div>
                                        <div className="text-xs text-gray-500">Machine: {doc.fileSyncId?.machineId}</div>
                                    </td>
                                    <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm text-gray-500 hidden md:table-cell">
                                        {formatFileSize(doc.fileSyncId?.fileSize || 0)}
                                    </td>
                                    <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm text-gray-500 hidden lg:table-cell">
                                        {doc.fileSyncId?.mimeType || 'Unknown'}
                                    </td>
                                    <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {doc.processingTime ? formatDuration(doc.processingTime) : '-'}
                                    </td>
                                    <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm text-gray-500 hidden md:table-cell">
                                        {doc.ocrConfidence ? `${doc.ocrConfidence.toFixed(1)}%` : '-'}
                                    </td>
                                    <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm font-medium">
                                        {doc.status === 'failed' && (
                                            <button
                                                onClick={() => retryDocument(doc._id)}
                                                className="text-blue-600 hover:text-blue-900 flex items-center space-x-1"
                                            >
                                                <ArrowPathIcon sx={{ width: 16, height: 16 }} />
                                                <span>Retry</span>
                                            </button>
                                        )}
                                        {doc.status === 'completed' && (
                                            <button className="text-green-600 hover:text-green-900">
                                                View
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                    </>
                )}

                {/* Pagination */}
                {totalPages > 1 && documents.length > 0 && (
                    <div className="bg-gray-50 px-3 sm:px-4 py-3 flex items-center justify-between">
                        <div className="flex-1 flex justify-between sm:hidden">
                            <button
                                onClick={() => setPage(Math.max(1, page - 1))}
                                disabled={page === 1}
                                className="relative inline-flex items-center px-3 py-2 border border-gray-300 text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Previous
                            </button>
                            <span className="text-sm text-gray-700">
                                {page} / {totalPages}
                            </span>
                            <button
                                onClick={() => setPage(Math.min(totalPages, page + 1))}
                                disabled={page === totalPages}
                                className="relative inline-flex items-center px-3 py-2 border border-gray-300 text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Next
                            </button>
                        </div>
                        <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                            <div>
                                <p className="text-sm text-gray-700">
                                    Page <span className="font-medium">{page}</span> of{' '}
                                    <span className="font-medium">{totalPages}</span>
                                </p>
                            </div>
                            <div>
                                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                                    <button
                                        onClick={() => setPage(Math.max(1, page - 1))}
                                        disabled={page === 1}
                                        className="relative inline-flex items-center px-3 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        Previous
                                    </button>
                                    <button
                                        onClick={() => setPage(Math.min(totalPages, page + 1))}
                                        disabled={page === totalPages}
                                        className="relative inline-flex items-center px-3 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        Next
                                    </button>
                                </nav>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Queue;